/**
 * VehiclesListPage - Listagem geral de veículos do tenant
 *
 * Página exclusiva do módulo Veículos (menu lateral "Veículos › Lista Veículos").
 * Usa os helpers padrão de listagem (ListPageLayout/Header/Kpis/FiltersBar/
 * Content/Footer) e busca 100% server-side via RPCs.
 *
 * @version 1.4.0
 * RELEASE: 2026-10-09 v1.5.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL).
 * RELEASE: 2026-10-03 v1.4.0 - Adiciona modo embutido e filtros pré-aplicados
 *          para os modais de auditoria do Dashboard de Veículos.
 * RELEASE: 2026-09-26 v1.3.0 - Placa volta ao texto simples anterior, sem o
 *          leiaute Mercosul compartilhado.
 * RELEASE: 2026-09-26 v1.2.0 - Placa usa o componente Mercosul compacto
 *          compartilhado com o Kanban de Vistorias.
 * RELEASE: 2026-08-08 v1.1.0 - Fase 4 (Multas): KPI de multas em aberto, badge
 *          por veículo e ação para abrir o modal de multas.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { VehicleStatusBadge, VehicleStatusSelect } from '@/components/vehicles/VehicleStatus';
import { useMemo, useState } from 'react';
import { Car, Bike, Truck, Package, Plus, Search, Pencil, Trash2, RefreshCw, ReceiptText, ArrowLeftRight } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageContent,
  ListPageFooter,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  KpiCard,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { useTenantVehicles, type TenantVehiclesFilters } from '@/hooks/useTenantVehicles';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import {
  useCustomerVehicles,
  VEHICLE_TYPE_LABELS,
  type CustomerVehicle,
  type VehicleType,
} from '@/hooks/useCustomerVehicles';
import { VehicleFormDialog } from '@/components/vehicles/VehicleFormDialog';
import { VehicleFinesDialog } from '@/components/vehicles/VehicleFinesDialog';
import { VehicleTypeCell } from '@/components/vehicles/VehicleTypeCell';
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import { VehicleTransferDialog } from '@/components/vehicles/VehicleTransferDialog';

import { usePermissionsContext } from '@/contexts/PermissionsContext';


/** Formata valores monetários em BRL */
const formatCurrency = (value: number | null) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

interface VehiclesListPageProps {
  /** Remove cabeçalho, KPIs e ações de manutenção ao renderizar dentro do modal. */
  embedded?: boolean;
  /** Recorte imutável originado pelo indicador clicado. */
  presetFilters?: TenantVehiclesFilters;
  /** Exibe a data da ocorrência em recortes de movimentação. */
  showActivity?: boolean;
}

/** Página de listagem de veículos do tenant */
export default function VehiclesListPage({ embedded = false, presetFilters, showActivity = false }: VehiclesListPageProps = {}) {
  const { activeCategories, finesEnabled } = useVehiclesModule();
  const { deleteVehicle } = useCustomerVehicles({}, false);
  const { canView, canEdit, isAdmin } = usePermissionsContext();
  // Transferência de propriedade exige permissão de edição do módulo de veículos
  const canTransferVehicle = isAdmin || canEdit('vehicles_list');

  // Fase 5: multas só ficam visíveis para admin ou perfis com a permissão
  const canViewFines = finesEnabled && (isAdmin || canView('vehicles_fines_view'));

  // Termo inicial vindo da pesquisa global do header (?q=)
  const [search, setSearch] = useState(
    () => new URLSearchParams(window.location.search).get('q') ?? '',
  );
  const [vehicleType, setVehicleType] = useState<VehicleType | 'ALL'>(presetFilters?.vehicleType ?? 'ALL');
  const [categoryId, setCategoryId] = useState<string>(presetFilters?.categoryId ?? 'ALL');
  // 2026-10-02: filtro por status do veículo
  const [statusId, setStatusId] = useState<string | null>(presetFilters?.statusId ?? null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [formOpen, setFormOpen] = useState(false);
  // Veículo selecionado para o modal de transferência de propriedade
  const [transferVehicle, setTransferVehicle] = useState<{
    id: string;
    plate: string | null;
    subtitle: string;
    ownerName: string | null;
  } | null>(null);
  const [editing, setEditing] = useState<CustomerVehicle | null>(null);
  // Veículo selecionado para o modal de multas
  const [finesVehicle, setFinesVehicle] = useState<{ id: string; plate: string | null; subtitle: string } | null>(null);
  // Veículo aberto no modal de detalhes (360º)
  const [detailsVehicleId, setDetailsVehicleId] = useState<string | null>(null);



  // Debounce simples da busca (evita disparar RPC a cada tecla)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);


  const { vehicles, total, kpis, isLoading, isFetching, refetch } = useTenantVehicles({
    ...presetFilters,
    search: debouncedSearch,
    vehicleType: vehicleType === 'ALL' ? null : vehicleType,
    categoryId: categoryId === 'ALL' ? null : categoryId,
    statusId,
    page,
    pageSize,
  });

  /** Abre o modal em modo criação */
  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  /** Abre o modal em modo edição */
  const handleEdit = (vehicle: CustomerVehicle) => {
    setEditing(vehicle);
    setFormOpen(true);
  };

  const rows = useMemo(() => vehicles, [vehicles]);

  return (
    <ListPageLayout className={embedded ? 'h-full min-h-0' : undefined}>
      {!embedded && <ListPageHeader
        title="Veículos"
        /* Busca premium centralizada na linha do título (padrão Retenção/TaskList).
           Oculta em mobile — nesse caso a busca fica na barra de filtros.
           `w-full` garante a largura total do slot central (mesmo tamanho da listagem de Negócios). */
        center={
          <div className="relative w-full max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder="Placa, chassi, renavam, marca ou modelo"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            {/* Atualizar somente com ícone, padronizado com as demais listagens */}
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching}
              title="Atualizar" aria-label="Atualizar">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
            <Button size="sm" onClick={handleNew} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Novo Veículo
            </Button>
          </div>
        }
      />}

      {!embedded && <ListPageKpis columns={canViewFines ? 6 : 5}>
        <KpiCard color="purple" icon={Car} label="Total" value={kpis?.total ?? 0} />
        <KpiCard color="blue" icon={Car} label="Carros" value={kpis?.cars ?? 0} />
        <KpiCard color="yellow" icon={Bike} label="Motos" value={kpis?.motorcycles ?? 0} />
        <KpiCard color="green" icon={Truck} label="Caminhões" value={kpis?.trucks ?? 0} />
        <KpiCard color="cyan" icon={Package} label="Outros" value={kpis?.others ?? 0} />
        {canViewFines && (
          <KpiCard
            color="orange"
            icon={ReceiptText}
            label="Multas em aberto"
            value={formatCurrency(kpis?.fines_open_total ?? 0)}
            suffix={`${kpis?.fines_vehicles ?? 0} veíc.`}
          />
        )}



      </ListPageKpis>}

      <ListPageFiltersBar isFetching={isFetching}>
        {/* Busca — apenas mobile; no desktop fica centralizada no cabeçalho */}
        <div className="relative flex-1 min-w-[160px] md:hidden">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            placeholder="Placa, chassi, renavam, marca ou modelo"
            className={cn(LIST_FILTERS_INPUT_CLASS, 'pl-7')}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

          {!presetFilters?.vehicleType && <Select
          value={vehicleType}
          onValueChange={(value) => {
            setVehicleType(value as VehicleType | 'ALL');
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[140px]')}>
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os tipos</SelectItem>
            {(Object.keys(VEHICLE_TYPE_LABELS) as VehicleType[]).map((type) => (
              <SelectItem key={type} value={type}>
                {VEHICLE_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
          </Select>}

        {!presetFilters?.categoryId && <Select
          value={categoryId}
          onValueChange={(value) => {
            setCategoryId(value);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[160px]')}>
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas as categorias</SelectItem>
            {activeCategories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>}

        {!presetFilters?.statusId && !presetFilters?.statusBase && <VehicleStatusSelect
          allowAll
          value={statusId}
          onChange={(v) => { setStatusId(v); setPage(1); }}
          className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[160px]')}
        />}
      </ListPageFiltersBar>

      <ListPageContent fullBleed className={embedded ? 'min-h-0 flex-1 overflow-auto' : undefined}>
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-center">
            <Car className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum veículo encontrado.</p>
            {!embedded && <Button size="sm" variant="outline" onClick={handleNew} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Cadastrar veículo
            </Button>}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Marca / Modelo</TableHead>
                <TableHead>Ano</TableHead>
                {showActivity && <TableHead>Movimentação</TableHead>}
                <TableHead>Cliente / Contato</TableHead>
                <TableHead className="text-right">Valor FIPE</TableHead>
                {canViewFines && <TableHead className="text-right">Multas</TableHead>}
                {!embedded && <TableHead className="w-[120px] text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((vehicle) => (
                <TableRow
                  key={vehicle.id}
                  className="cursor-pointer"
                  onClick={() => setDetailsVehicleId(vehicle.id)}
                >
                  <TableCell className="font-medium">{vehicle.plate || '—'}</TableCell>
                  <TableCell><VehicleStatusBadge statusId={vehicle.status_id} /></TableCell>
                  <TableCell>
                    {/* v1.2.0 — ícone + nome do tipo */}
                    <VehicleTypeCell
                      type={vehicle.vehicle_type}
                      categoryName={vehicle.category_name}
                    />
                  </TableCell>

                  <TableCell className="max-w-[280px] truncate">
                    {[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · ') || '—'}
                  </TableCell>
                  <TableCell>{vehicle.year ?? '—'}</TableCell>
                  {showActivity && (
                    <TableCell className="whitespace-nowrap">
                      {vehicle.activity_at ? new Date(vehicle.activity_at).toLocaleString('pt-BR') : '—'}
                    </TableCell>
                  )}
                  <TableCell className="max-w-[220px] truncate">
                    {vehicle.customer_name || vehicle.contact_name || (
                      <span className="text-muted-foreground">Avulso</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(vehicle.fipe_value)}</TableCell>
                  {/* Fase 4: badge de multas em aberto */}
                  {canViewFines && (
                    <TableCell className="text-right">
                      {Number(vehicle.fines_total_open ?? 0) > 0 ? (
                        <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/15">
                          {formatCurrency(Number(vehicle.fines_total_open))}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {vehicle.fines_synced_at ? 'Sem multas' : '—'}
                        </span>
                      )}
                    </TableCell>
                  )}
                  {!embedded && <TableCell className="text-right">
                    {/* Ações não devem abrir o modal de detalhes */}
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>

                      {canViewFines && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="Multas do veículo"
                          onClick={() =>
                            setFinesVehicle({
                              id: vehicle.id,
                              plate: vehicle.plate,
                              subtitle:
                                [vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · '),
                            })
                          }
                        >
                          <ReceiptText className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canTransferVehicle && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="Transferir veículo"
                          onClick={() =>
                            setTransferVehicle({
                              id: vehicle.id,
                              plate: vehicle.plate,
                              subtitle: [vehicle.brand_name, vehicle.model_name, vehicle.year]
                                .filter(Boolean)
                                .join(' · '),
                              ownerName: vehicle.customer_name || vehicle.contact_name || null,
                            })
                          }
                        >
                          <ArrowLeftRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => handleEdit(vehicle as unknown as CustomerVehicle)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        disabled={deleteVehicle.isPending}
                        onClick={() => deleteVehicle.mutate(vehicle.id, { onSuccess: () => refetch() })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ListPageFooter>

      {!embedded && <VehicleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        scope={{}}
        allowOwnerSelection
        vehicle={editing}
        onSaved={() => refetch()}
      />}

      {/* Fase 4: modal de multas do veículo selecionado */}
      {/* Transferência de propriedade a partir da listagem */}
      {transferVehicle && (
        <VehicleTransferDialog
          open={!!transferVehicle}
          onOpenChange={(o) => !o && setTransferVehicle(null)}
          vehicleId={transferVehicle.id}
          plate={transferVehicle.plate}
          subtitle={transferVehicle.subtitle}
          currentOwnerName={transferVehicle.ownerName}
          onTransferred={() => refetch()}
        />
      )}

      <VehicleFinesDialog
        open={!!finesVehicle}
        onOpenChange={(open) => !open && setFinesVehicle(null)}
        vehicleId={finesVehicle?.id ?? null}
        plate={finesVehicle?.plate ?? null}
        subtitle={finesVehicle?.subtitle}
        canSync={canViewFines}
        onSynced={() => refetch()}
      />

      {/* Modal 360º do veículo (abre ao clicar na linha) */}
      <VehicleDetailsModalPremium
        vehicleId={detailsVehicleId}
        open={!!detailsVehicleId}
        onOpenChange={(open) => !open && setDetailsVehicleId(null)}
        onEdit={(id) => {
          const target = rows.find((item) => item.id === id);
          if (target) {
            setDetailsVehicleId(null);
            handleEdit(target as unknown as CustomerVehicle);
          }
        }}
      />
    </ListPageLayout>

  );
}
